import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TablePagination,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  DirectionsBus,
  Person,
  Route as RouteIcon,
  AttachMoney,
  PlayArrow,
  Stop,
  Visibility,
  Schedule,
  CheckCircle,
  Cancel,
  MonetizationOn,
} from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Redondos
 * Path: backoffice-web/src/pages/Redondos/Redondos.js
 * Última modificación: 2025-01-02 06:00
 * Dev: cpalacios01@gmail.com | +595994648273
 */

// TabPanel Component
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Redondos() {
  const [currentTab, setCurrentTab] = useState(0);
  const [redondos, setRedondos] = useState([]);
  const [redondosAbiertos, setRedondosAbiertos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [itinerarios, setItinerarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openDetalleDialog, setOpenDetalleDialog] = useState(false);
  const [detalleRedondo, setDetalleRedondo] = useState(null);
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Formulario para abrir redondo
  const [formAbrir, setFormAbrir] = useState({
    chofer_id: '',
    bus_id: '',
    itinerario_id: '',
    monto_inicial: '',
    fecha_salida: new Date().toISOString().slice(0, 16),
  });
  
  // Formulario para cerrar redondo
  const [formCerrar, setFormCerrar] = useState({
    redondo_id: '',
    monto_entregado: '',
    observaciones: '',
  });
  
  useEffect(() => {
    fetchInitialData();
  }, []);
  
  useEffect(() => {
    if (currentTab === 1) {
      fetchRedondosAbiertos();
    } else if (currentTab === 2) {
      fetchRedondosAbiertos();
    } else if (currentTab === 3) {
      fetchRedondos();
    }
  }, [currentTab]);
  
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [choferesRes, busesRes, itinerariosRes] = await Promise.all([
        api.get('/choferes-disponibles-redondo'), // CAMBIADO: Solo choferes sin redondo abierto
        api.get('/buses-disponibles-redondo'), // CAMBIADO: Solo buses activos sin redondo abierto
        api.get('/itinerarios/activos'),
      ]);
      
      // Manejar diferentes formatos de respuesta
      const choferesData = Array.isArray(choferesRes.data) 
        ? choferesRes.data 
        : (choferesRes.data.data || []);
      const busesData = Array.isArray(busesRes.data) 
        ? busesRes.data 
        : (busesRes.data.data || []);
      const itinerariosData = Array.isArray(itinerariosRes.data) 
        ? itinerariosRes.data 
        : (itinerariosRes.data.data || []);
      
      setChoferes(choferesData);
      setBuses(busesData);
      setItinerarios(itinerariosData);
    } catch (error) {
      showSnackbar('Error al cargar datos iniciales', 'error');
      console.error('Error:', error);
      // Asegurar que siempre haya arrays
      setChoferes([]);
      setBuses([]);
      setItinerarios([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRedondos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/redondos');
      // Manejar diferentes formatos de respuesta
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setRedondos(data);
    } catch (error) {
      showSnackbar('Error al cargar redondos', 'error');
      console.error('Error:', error);
      setRedondos([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };
  
  const fetchRedondosAbiertos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/redondos/abiertos');
      // Manejar diferentes formatos de respuesta
      const data = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      setRedondosAbiertos(data);
    } catch (error) {
      showSnackbar('Error al cargar redondos abiertos', 'error');
      console.error('Error:', error);
      setRedondosAbiertos([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };
  
  const handleAbrirRedondo = async () => {
    try {
      // Validaciones
      if (!formAbrir.chofer_id || !formAbrir.bus_id || !formAbrir.itinerario_id) {
        showSnackbar('Todos los campos son requeridos', 'warning');
        return;
      }
      
      setLoading(true);
      const response = await api.post('/redondos', {
        chofer_id: formAbrir.chofer_id,
        bus_id: formAbrir.bus_id,
        itinerario_id: formAbrir.itinerario_id,
        monto_inicial: parseFloat(formAbrir.monto_inicial) || 0,
        fecha_salida: formAbrir.fecha_salida,
      });
      
      showSnackbar(
        `Redondo ${response.data.redondo.codigo_unico} abierto exitosamente`,
        'success'
      );
      
      // Limpiar formulario
      setFormAbrir({
        chofer_id: '',
        bus_id: '',
        itinerario_id: '',
        monto_inicial: '',
        fecha_salida: new Date().toISOString().slice(0, 16),
      });
      
      // Cambiar a la pestaña de redondos abiertos
      setCurrentTab(1);
      fetchRedondosAbiertos();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al abrir redondo',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCerrarRedondo = async () => {
    try {
      if (!formCerrar.redondo_id || !formCerrar.monto_entregado) {
        showSnackbar('Debe seleccionar un redondo e ingresar el monto entregado', 'warning');
        return;
      }
      
      setLoading(true);
      await api.put(`/redondos/${formCerrar.redondo_id}/cerrar`, {
        monto_entregado: parseFloat(formCerrar.monto_entregado),
        observaciones: formCerrar.observaciones,
      });
      
      showSnackbar('Redondo cerrado y rendido exitosamente', 'success');
      
      // Limpiar formulario
      setFormCerrar({
        redondo_id: '',
        monto_entregado: '',
        observaciones: '',
      });
      
      fetchRedondosAbiertos();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || 'Error al cerrar redondo',
        'error'
      );
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerDetalle = async (redondoId) => {
    try {
      setLoading(true);
      const response = await api.get(`/redondos/${redondoId}`);
      setDetalleRedondo(response.data);
      setOpenDetalleDialog(true);
    } catch (error) {
      showSnackbar('Error al cargar detalle', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const formatMonto = (monto) => {
    return new Intl.NumberFormat('es-PY', {
      style: 'currency',
      currency: 'PYG',
      minimumFractionDigits: 0,
    }).format(monto);
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsBus />
          Gestión de Redondos
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Administración completa del sistema de redondos
        </Typography>
      </Box>
      
      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Abrir Redondo" icon={<PlayArrow />} />
          <Tab label="Redondos Abiertos" icon={<DirectionsBus />} />
          <Tab label="Cerrar Redondo" icon={<Stop />} />
          <Tab label="Historial" icon={<Schedule />} />
        </Tabs>
      </Paper>
      
      {/* Tab 1: Abrir Redondo */}
      <TabPanel value={currentTab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrow color="success" />
            Abrir Nuevo Redondo
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Chofer</InputLabel>
                <Select
                  value={formAbrir.chofer_id}
                  onChange={(e) => setFormAbrir({ ...formAbrir, chofer_id: e.target.value })}
                  label="Chofer"
                >
                  {choferes.map((chofer) => (
                    <MenuItem key={chofer.id} value={chofer.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" />
                        {chofer.nombre || chofer.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Bus</InputLabel>
                <Select
                  value={formAbrir.bus_id}
                  onChange={(e) => setFormAbrir({ ...formAbrir, bus_id: e.target.value })}
                  label="Bus"
                >
                  {buses.map((bus) => (
                    <MenuItem key={bus.id} value={bus.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DirectionsBus fontSize="small" />
                        Bus {bus.numero} - {bus.patente}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Itinerario</InputLabel>
                <Select
                  value={formAbrir.itinerario_id}
                  onChange={(e) => setFormAbrir({ ...formAbrir, itinerario_id: e.target.value })}
                  label="Itinerario"
                >
                  {itinerarios.map((itinerario) => (
                    <MenuItem key={itinerario.id} value={itinerario.id}>
                      <Box>
                        <Typography variant="body2">{itinerario.nombre}</Typography>
                        {itinerario.distancia_km && (
                          <Typography variant="caption" color="textSecondary">
                            {itinerario.distancia_km} km - {itinerario.duracion_estimada} min
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Monto Inicial"
                type="number"
                value={formAbrir.monto_inicial}
                onChange={(e) => setFormAbrir({ ...formAbrir, monto_inicial: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Gs.</InputAdornment>,
                }}
                helperText="Dinero para vueltos"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha y Hora de Salida"
                type="datetime-local"
                value={formAbrir.fecha_salida}
                onChange={(e) => setFormAbrir({ ...formAbrir, fecha_salida: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                onClick={handleAbrirRedondo}
                disabled={loading}
              >
                Abrir Redondo
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>
      
      {/* Tab 2: Redondos Abiertos */}
      <TabPanel value={currentTab} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Chofer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bus</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Itinerario</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Hora Salida</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Monto Inicial</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Cargando redondos abiertos...</Typography>
                  </TableCell>
                </TableRow>
              ) : redondosAbiertos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">
                      No hay redondos abiertos actualmente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                redondosAbiertos.map((redondo) => (
                  <TableRow key={redondo.id} hover>
                    <TableCell>
                      <Chip
                        label={redondo.codigo_unico}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{redondo.chofer?.nombre || redondo.chofer?.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DirectionsBus fontSize="small" color="action" />
                        <Typography variant="body2">
                          {redondo.bus?.numero}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{redondo.itinerario?.nombre}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(redondo.fecha_salida).toLocaleString('es-PY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatMonto(redondo.monto_inicial)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleVerDetalle(redondo.id)}
                        title="Ver Detalle"
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
      
      {/* Tab 3: Cerrar Redondo */}
      <TabPanel value={currentTab} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Stop color="error" />
            Cerrar Redondo
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Seleccionar Redondo Abierto</InputLabel>
                <Select
                  value={formCerrar.redondo_id}
                  onChange={(e) => setFormCerrar({ ...formCerrar, redondo_id: e.target.value })}
                  label="Seleccionar Redondo Abierto"
                >
                  {redondosAbiertos.map((redondo) => (
                    <MenuItem key={redondo.id} value={redondo.id}>
                      <Box>
                        <Typography variant="body2">
                          {redondo.codigo_unico} - {redondo.chofer?.nombre || redondo.chofer?.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Bus {redondo.bus?.numero} | {redondo.itinerario?.nombre}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Monto Entregado por el Chofer"
                type="number"
                value={formCerrar.monto_entregado}
                onChange={(e) => setFormCerrar({ ...formCerrar, monto_entregado: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Gs.</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Observaciones"
                value={formCerrar.observaciones}
                onChange={(e) => setFormCerrar({ ...formCerrar, observaciones: e.target.value })}
                multiline
                rows={1}
                placeholder="Notas adicionales..."
              />
            </Grid>
            
            {formCerrar.redondo_id && (
              <Grid item xs={12}>
                <Alert severity="info">
                  El sistema calculará automáticamente la diferencia entre el monto declarado 
                  y el monto esperado (inicial + boletas - gastos).
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Stop />}
                onClick={handleCerrarRedondo}
                disabled={loading || !formCerrar.redondo_id}
              >
                Cerrar Redondo y Generar Rendición
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>
      
      {/* Tab 4: Historial */}
      <TabPanel value={currentTab} index={3}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Chofer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bus</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fecha</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : redondos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">
                      No hay redondos registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                redondos
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((redondo) => (
                    <TableRow key={redondo.id} hover>
                      <TableCell>
                        <Chip
                          label={redondo.codigo_unico}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{redondo.chofer?.nombre || redondo.chofer?.name}</TableCell>
                      <TableCell>Bus {redondo.bus?.numero}</TableCell>
                      <TableCell>
                        {new Date(redondo.fecha_salida).toLocaleDateString('es-PY')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={redondo.estado.toUpperCase()}
                          color={
                            redondo.estado === 'abierto' ? 'success' :
                            redondo.estado === 'cerrado' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleVerDetalle(redondo.id)}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={redondos.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas por página:"
          />
        </TableContainer>
      </TabPanel>
      
      {/* Dialog Detalle */}
      <Dialog
        open={openDetalleDialog}
        onClose={() => setOpenDetalleDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalle del Redondo {detalleRedondo?.codigo_unico}
        </DialogTitle>
        <DialogContent>
          {detalleRedondo && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Información General
                    </Typography>
                    <Typography variant="body2">
                      <strong>Chofer:</strong> {detalleRedondo.chofer?.nombre || detalleRedondo.chofer?.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Bus:</strong> {detalleRedondo.bus?.numero} - {detalleRedondo.bus?.patente}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Itinerario:</strong> {detalleRedondo.itinerario?.nombre}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Estado:</strong>{' '}
                      <Chip label={detalleRedondo.estado.toUpperCase()} size="small" />
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Montos
                    </Typography>
                    <Typography variant="body2">
                      <strong>Monto Inicial:</strong> {formatMonto(detalleRedondo.monto_inicial)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Recaudado:</strong> {formatMonto(detalleRedondo.total_recaudado || 0)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Gastos:</strong> {formatMonto(detalleRedondo.total_gastos || 0)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="primary">
                      <strong>Debe Entregar:</strong> {formatMonto(detalleRedondo.debe_entregar || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Boletas: {detalleRedondo.total_boletas || 0}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Gastos: {detalleRedondo.gastos?.length || 0}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetalleDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
